/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Message Form With Agent Container
 */

import {
  checkLoggedIn,
  getUser,
  selectIsMobile,
  selectIsSendingMessage,
  selectNow,
} from '@zcode-ui/web-zumper-global-state'
import { type AnalyticsC, type Origin, trackGAEvent } from '@zcode/nr-utils'
import type { APPLY, MESSAGE, TOUR, WAITLIST } from '@zcode/nr/es/shared/constants/message'
import { selectSimilarListables } from '@zcode/nr/es/shared/modules/similarListings/selectors'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import { trackOptimizelyPhoneEvents } from '@zcode/nr/es/shared/thirdParty/optimizely/thunks'
import { memoMapDispatchToProps } from '@zcode/react-utils'
import type { Listable, MessageModalDataNonEmpty, Property, State } from '@zcode/state-type'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import { getMessageSentTime, getMessaged } from '../../../inspectors/index'
import { selectFilterBedroom, selectFilters } from '../../../modules/filters/selectors/index'
import { mixpanelCall } from '../../../modules/mixpanel/thunks/mixpanelCall'
import { selectCtaName } from '../../../modules/modals/selectors/messageModal/index'
import {
  selectSelectedTime,
  selectSelectedTourType,
  selectSubmitting,
} from '../../../modules/multifamilyTourTimePicker/selectors/index'
import { ERROR_MESSAGE } from '../../../packages/optimizely/constants/events'
import { trackCallEvent } from '../../../utils/trackCallEvent'
import {
  MessageFormInnerContainer,
  type MessageFormInnerContainerProps,
} from './MessageFormInnerContainer'
import { getRecentLeadCounts } from './helpers/getShouldLimitLeads'

interface MessageFormContainerProps {
  analyticChannel:
    | MessageModalDataNonEmpty['analyticChannel']
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.PERMANENT_ADDRESS
  ctaName?: string
  currentTab?: string
  entity: Listable | Property
  equalCTAIntent?: typeof APPLY | typeof MESSAGE | typeof TOUR | typeof WAITLIST
  feedName?: string | null
  isWaitlistForm?: boolean
  origin: Origin
}

const mapStateToProps = (state: State, ownProps: MessageFormContainerProps) => {
  const { entity } = ownProps
  const messaged = getMessaged(state)
  const { longTermLeadCount, shortTermLeadCount } = getRecentLeadCounts(messaged)

  return {
    equalCTAIntent: ownProps.isWaitlistForm ? 'Waitlist' : ownProps.equalCTAIntent,
    filters: selectFilters(state),
    filtersBedroom: selectFilterBedroom(state),
    isLoggedIn: checkLoggedIn(state),
    isMobile: selectIsMobile(state),
    isSendingMessage: selectIsSendingMessage(state),
    longTermLeadCount,
    ctaName: ownProps.ctaName || selectCtaName(state),
    messageSentTime: getMessageSentTime(state, entity),
    now: selectNow(state),
    selectedTime: selectSelectedTime(state),
    shortTermLeadCount,
    similarListings: selectSimilarListables(state),
    submittingTourRequest: selectSubmitting(state),
    tourType: selectSelectedTourType(state),
    user: getUser(state),
  } satisfies Partial<MessageFormInnerContainerProps<Listable | Property, Origin>>
}

const mapDispatchToProps = (dispatch: Dispatch, ownProps: MessageFormContainerProps) => {
  const { analyticChannel, entity, feedName } = ownProps
  return {
    onPaidPhoneClick: () => {
      trackCallEvent(entity)
      trackGAEvent(analyticChannel, 'Phone', feedName)
      dispatch(trackOptimizelyPhoneEvents({ entity }))
      dispatch(mixpanelCall({ entity, origin: ownProps.origin }))
    },
    onSubmitError: () => {
      void dispatch(Optimizely.track({ eventKey: ERROR_MESSAGE }))
    },
  } satisfies Partial<MessageFormInnerContainerProps<Listable | Property, Origin>>
}

const memoMapMessageFormDispatchToProps = memoMapDispatchToProps(mapDispatchToProps, [
  'analyticChannel',
  'entity',
  'feedName',
])

export default connect(
  mapStateToProps,
  memoMapMessageFormDispatchToProps
)(MessageFormInnerContainer)
